import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import {
  Formik,
  Form,
  Field,
  ErrorMessage,
} from "formik";

import {
  FiArrowLeft,
  FiArrowRight,
  FiShield,
  FiUsers,
  FiMessageCircle,
  FiCalendar,
  FiMail,
} from "react-icons/fi";

import ToastMessage from "../../components/ToastMessage";
import Input from "../../components/ui/Input";
import { useUserContext } from "../../context/AuthContext";
import { APP_NAME } from "../../constants";

import {
  signIn,
  signUp,
  forgotPassword as forgotPasswordRequest,
} from "../../api";

const Auth = () => {
  const initialState = {
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    birthdate: "",
    gender: "",
  };

  const { checkAuth } = useUserContext();

  const [isSignup, setIsSignup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState("");

  const navigate = useNavigate();

  /* =========================================================
     SWITCH LOGIN / REGISTER
  ========================================================= */

  const switchMode = () => {
    setIsSignup((prev) => !prev);
    setShowPassword(false);
    setMessage("");
    setForgotPasswordMode(false);
    setForgotSuccess("");
  };

  /* =========================================================
     FORGOT PASSWORD
  ========================================================= */

  const openForgotPassword = () => {
    setForgotPasswordMode(true);
    setIsSignup(false);
    setShowPassword(false);
    setMessage("");
    setForgotSuccess("");
  };

  const closeForgotPassword = () => {
    setForgotPasswordMode(false);
    setMessage("");
    setForgotSuccess("");
  };

  const forgotPasswordValidationSchema = Yup.object().shape({
    email: Yup.string()
      .required("Email is required")
      .email("Invalid email format"),
  });

  const handleForgotPassword = async (
    values,
    { setSubmitting }
  ) => {
    setMessage("");
    setForgotSuccess("");

    try {
      const res = await forgotPasswordRequest({
        email: values.email,
      });

      setForgotSuccess(
        res.data.message ||
          "If an account exists for that email, a password reset link has been sent."
      );
    } catch (err) {
      console.error("Forgot password error:", err);

      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.warning;

      setMessage(
        backendMessage ||
          "Unable to send the password reset email. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* =========================================================
     LOGIN / REGISTER VALIDATION
  ========================================================= */

  const validationSchema = (signup) => {
    return Yup.object().shape({
      firstname: Yup.string().when([], {
        is: () => signup === true,
        then: (schema) =>
          schema
            .required("First name is required")
            .min(2, "First name needs at least 2 characters"),
      }),

      lastname: Yup.string().when([], {
        is: () => signup === true,
        then: (schema) =>
          schema
            .required("Last name is required")
            .min(2, "Last name needs at least 2 characters"),
      }),

      email: Yup.string()
        .required("Email is required")
        .email("Invalid email format"),

      password: Yup.string()
        .required("Password is required")
        .min(2, "Password needs to be at least 2 characters"),

      birthdate: Yup.string().when([], {
        is: () => signup === true,
        then: (schema) =>
          schema.required("Birthdate is required"),
      }),

      gender: Yup.string().when([], {
        is: () => signup === true,
        then: (schema) =>
          schema.required("Please select your gender"),
      }),
    });
  };

  /* =========================================================
     LOGIN / REGISTER
  ========================================================= */

  const onSubmit = async (
    values,
    { setSubmitting, resetForm }
  ) => {
    setMessage("");

    try {
      /* =====================================================
         REGISTER
      ===================================================== */

      if (isSignup) {
        const res = await signUp(values);

        if (res.data.warning) {
          setMessage(res.data.warning);
          return;
        }

        if (res.data.message) {
          ToastMessage(res.data.message);
        }

        resetForm();
        setShowPassword(false);
        setMessage("");
        setIsSignup(false);

        return;
      }

      /* =====================================================
         LOGIN
      ===================================================== */

      const { email, password } = values;

      const res = await signIn({
        email,
        password,
      });

      if (res.data.warning) {
        setMessage(res.data.warning);
        return;
      }

      /*
       * Login has succeeded.
       *
       * The backend has now created the HTTP-only
       * authentication cookie.
       *
       * Verify that cookie and load the user before
       * navigating to the home page.
       */

      const authenticatedUser = await checkAuth();

      if (!authenticatedUser) {
        setMessage(
          "Your session could not be verified. Please try again."
        );

        return;
      }

      if (res.data.message) {
        ToastMessage(res.data.message);
      }

      navigate("/", {
        replace: true,
      });
    } catch (err) {
      console.error("Authentication error:", err);

      if (err?.response) {
        const backendMessage =
          err.response.data?.warning ||
          err.response.data?.message;

        setMessage(
          backendMessage ||
            "Something went wrong. Please try again."
        );
      } else if (err?.request) {
        setMessage(
          "Unable to connect to the server. Please make sure the backend is running."
        );
      } else {
        setMessage(
          "Something went wrong. Please try again."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className="h-[100dvh] w-full overflow-hidden bg-gray-50 lg:grid lg:grid-cols-2">

      {/* =====================================================
          LEFT SIDE
      ===================================================== */}

      <section className="relative hidden h-[100dvh] overflow-hidden bg-gradient-to-br from-purple-700 via-purple-600 to-indigo-700 px-12 py-8 text-white lg:flex lg:flex-col lg:justify-between">

        <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />

        <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-pink-400/20 blur-3xl" />

        <div className="absolute right-20 top-1/3 h-40 w-40 rounded-full bg-indigo-300/20 blur-3xl" />

        {/* LOGO */}

        <div className="relative z-10">
          <div className="inline-flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-lg">
              <img
                src="/assets/images/logo.png"
                alt="Alakoze"
                className="h-10 w-10 object-contain"
              />
            </div>

            <span className="text-xl font-bold tracking-tight">
              {APP_NAME}
            </span>
          </div>
        </div>

        {/* MARKETING */}

        <div className="relative z-10 max-w-lg">
          <span className="inline-flex rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/90 backdrop-blur-md">
            Connect. Share. Discover.
          </span>

          <h1 className="mt-5 text-4xl font-bold leading-tight tracking-tight xl:text-5xl">
            Your people.
            <br />
            Your moments.
            <br />
            One community.
          </h1>

          <p className="mt-4 max-w-md text-[15px] leading-6 text-purple-100">
            Share what matters, connect with people you care about,
            and discover new conversations in one place.
          </p>

          <div className="mt-6 space-y-3">

            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 backdrop-blur-md">
                <FiUsers size={18} />
              </div>

              <div>
                <p className="text-sm font-semibold">
                  Build your network
                </p>

                <p className="mt-0.5 text-xs text-purple-200">
                  Follow people and grow your community.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 backdrop-blur-md">
                <FiMessageCircle size={18} />
              </div>

              <div>
                <p className="text-sm font-semibold">
                  Share every format
                </p>

                <p className="mt-0.5 text-xs text-purple-200">
                  Text, photos, videos, voice, comments and replies.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 backdrop-blur-md">
                <FiShield size={18} />
              </div>

              <div>
                <p className="text-sm font-semibold">
                  Your account, your space
                </p>

                <p className="mt-0.5 text-xs text-purple-200">
                  Sign in and continue where you left off.
                </p>
              </div>
            </div>

          </div>
        </div>

        <p className="relative z-10 text-xs text-purple-200">
          © {new Date().getFullYear()} {APP_NAME}
        </p>
      </section>

      {/* =====================================================
          RIGHT SIDE
      ===================================================== */}

      <section className="h-[100dvh] overflow-y-auto">
        <div className="flex min-h-full items-center justify-center px-4 py-6 sm:px-6 lg:px-10">

          <div className="w-full max-w-md">

            {/* MOBILE LOGO */}

            <div className="mb-6 flex items-center justify-center gap-2 lg:hidden">
              <img
                src="/assets/images/logo.png"
                alt="Alakoze"
                className="h-12 w-12 object-contain"
              />

              <span className="text-xl font-bold text-gray-900">
                {APP_NAME}
              </span>
            </div>

            {/* =================================================
                FORGOT PASSWORD
            ================================================= */}

            {forgotPasswordMode ? (
              <>
                <button
                  type="button"
                  onClick={closeForgotPassword}
                  className="mb-5 flex items-center gap-2 text-sm font-semibold text-gray-500 transition hover:text-purple-600"
                >
                  <FiArrowLeft size={17} />
                  Back to sign in
                </button>

                <div className="mb-7">
                  <span className="inline-block rounded-full bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-600">
                    Account recovery
                  </span>

                  <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900">
                    Forgot your password?
                  </h2>

                  <p className="mt-2 text-sm leading-6 text-gray-400">
                    Enter your email address and we'll send you a
                    secure link to reset your password.
                  </p>
                </div>

                {message && (
                  <div className="mb-5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
                    {message}
                  </div>
                )}

                {forgotSuccess && (
                  <div className="mb-5 rounded-xl border border-green-100 bg-green-50 px-4 py-3 text-sm font-medium leading-6 text-green-700">
                    {forgotSuccess}
                  </div>
                )}

                <Formik
                  initialValues={{
                    email: "",
                  }}
                  validationSchema={
                    forgotPasswordValidationSchema
                  }
                  onSubmit={
                    handleForgotPassword
                  }
                >
                  {({ isSubmitting }) => (
                    <Form className="space-y-5">
                      <Input
                        label="Email address"
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                      />

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="group flex w-full items-center justify-center gap-2 rounded-xl bg-purple-600 px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-purple-700 hover:shadow-md active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isSubmitting
                          ? "Sending..."
                          : "Send reset link"}

                        {!isSubmitting && (
                          <FiMail size={17} />
                        )}
                      </button>
                    </Form>
                  )}
                </Formik>
              </>
            ) : (
              <>

                {/* =================================================
                    LOGIN / REGISTER HEADER
                ================================================= */}

                <div className="mb-6">
                  <span className="inline-block rounded-full bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-600">
                    {isSignup
                      ? `Join ${APP_NAME}`
                      : "Welcome back"}
                  </span>

                  <h2 className="mt-3 text-3xl font-bold tracking-tight text-gray-900">
                    {isSignup
                      ? "Create your account"
                      : "Sign in to your account"}
                  </h2>

                  <p className="mt-1.5 text-sm leading-6 text-gray-400">
                    {isSignup
                      ? "Tell us a little about yourself and start connecting."
                      : `Enter your details to continue to ${APP_NAME}.`}
                  </p>
                </div>

                {/* ERROR */}

                {message && (
                  <div className="mb-5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
                    {message}
                  </div>
                )}

                {/* =================================================
                    LOGIN / REGISTER FORM
                ================================================= */}

                <Formik
                  initialValues={initialState}
                  validationSchema={
                    validationSchema(isSignup)
                  }
                  onSubmit={onSubmit}
                  enableReinitialize
                >
                  {({ isSubmitting }) => (
                    <Form className="space-y-4">

                      {/* SIGNUP FIELDS */}

                      {isSignup && (
                        <>
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <Input
                              label="First name"
                              name="firstname"
                              type="text"
                              placeholder="Mark"
                            />

                            <Input
                              label="Last name"
                              name="lastname"
                              type="text"
                              placeholder="Griffin"
                            />
                          </div>

                          {/* BIRTHDATE */}

                          <div>
                            <label
                              htmlFor="birthdate"
                              className="mb-2 block text-sm font-semibold text-gray-700"
                            >
                              Birthdate
                            </label>

                            <div className="relative">
                              <Field
                                id="birthdate"
                                name="birthdate"
                                type="date"
                                className="block w-full rounded-xl border border-gray-200 bg-white px-4 py-3 pr-11 text-sm text-gray-900 outline-none transition-all focus:border-purple-300 focus:ring-4 focus:ring-purple-50"
                              />

                              <FiCalendar
                                size={18}
                                className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                              />
                            </div>

                            <ErrorMessage
                              name="birthdate"
                              component="div"
                              className="mt-1.5 text-xs font-medium text-red-500"
                            />
                          </div>

                          {/* GENDER */}

                          <div>
                            <label
                              htmlFor="gender"
                              className="mb-2 block text-sm font-semibold text-gray-700"
                            >
                              Gender
                            </label>

                            <Field
                              as="select"
                              id="gender"
                              name="gender"
                              className="block w-full cursor-pointer rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition-all focus:border-purple-300 focus:ring-4 focus:ring-purple-50"
                            >
                              <option value="">
                                Select gender
                              </option>

                              <option value="male">
                                Male
                              </option>

                              <option value="female">
                                Female
                              </option>

                              <option value="other">
                                Other
                              </option>

                              <option value="prefer_not_to_say">
                                Prefer not to say
                              </option>
                            </Field>

                            <ErrorMessage
                              name="gender"
                              component="div"
                              className="mt-1.5 text-xs font-medium text-red-500"
                            />
                          </div>
                        </>
                      )}

                      {/* EMAIL */}

                      <Input
                        label="Email address"
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                      />

                      {/* PASSWORD */}

                      <Input
                        label="Password"
                        name="password"
                        type={
                          showPassword
                            ? "text"
                            : "password"
                        }
                        handleShowPassword={
                          handleShowPassword
                        }
                        placeholder="Enter your password"
                      />

                      {/* FORGOT PASSWORD */}

                      {!isSignup && (
                        <div className="-mt-2 flex justify-end">
                          <button
                            type="button"
                            onClick={
                              openForgotPassword
                            }
                            className="text-xs font-semibold text-purple-600 transition hover:text-purple-700 hover:underline"
                          >
                            Forgot password?
                          </button>
                        </div>
                      )}

                      {/* SUBMIT */}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="group flex w-full items-center justify-center gap-2 rounded-xl bg-purple-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-purple-700 hover:shadow-md active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isSubmitting
                          ? isSignup
                            ? "Creating account..."
                            : "Signing in..."
                          : isSignup
                            ? "Create account"
                            : "Sign in"}

                        {!isSubmitting && (
                          <FiArrowRight
                            size={17}
                            className="transition-transform group-hover:translate-x-0.5"
                          />
                        )}
                      </button>
                    </Form>
                  )}
                </Formik>

                {/* SWITCH */}

                <div className="mt-5 text-center">
                  <p className="text-sm text-gray-500">
                    {isSignup
                      ? "Already have an account?"
                      : "Don't have an account?"}

                    <button
                      type="button"
                      onClick={switchMode}
                      className="ml-1.5 font-semibold text-purple-600 transition hover:text-purple-700 hover:underline"
                    >
                      {isSignup
                        ? "Sign in"
                        : "Create account"}
                    </button>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Auth;